import { DriveQuotaSettingsCard } from './DriveQuotaSettingsCard';
import { HostingBackupSettingsCard } from './HostingBackupCatalogCard';
import { useEffect, useState } from 'react';
import type {
  GoogleDriveIntegrationView,
  GoogleDriveQuotaView,
  GoogleDriveStorageProbeView,
  GoogleDriveStorageCheckResults,
  GoogleDriveHistoryEntry,
} from '@tosho/contracts';
import { PrototypeIcon } from './PrototypeIcon';
import { googleDriveApi, GoogleDriveApiError } from './google-drive-api';
import { scrollToSection } from './scroll-to-section';
import { DriveVideoProbeCard, videoCheckLabels } from './DriveVideoProbeCard';
import { DriveClientFolderCheckCard, clientFolderCheckLabels } from './DriveClientFolderCheckCard';
import '../styles/modules-drive-integration.css';

interface Props {
  readonly csrfToken: string;
  readonly onBack: () => void;
  readonly onReauthenticate: () => void;
}

const dateFormatter = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
const labels: Readonly<Record<string, string>> = {
  PENDING: 'Autorisation à terminer chez Google',
  RECEIVED: 'Prêt à terminer dans Tosho',
  EXCHANGING: 'Vérification en cours',
  COMPLETED: 'Connexion terminée',
  FAILED: 'Connexion non établie',
  CANCELLED: 'Tentative annulée',
  EXPIRED: 'Délai dépassé',
};

/** Repères des cartes Freebe, avec un parcours Google natif et des textes orientés action. */
export function GoogleDriveIntegrationPanel({
  csrfToken,
  onBack,
  onReauthenticate,
}: Props): React.JSX.Element {
  const [quota, setQuota] = useState<GoogleDriveQuotaView | null>(null);
  const [quotaError, setQuotaError] = useState(false);
  const [measuringQuota, setMeasuringQuota] = useState(false);
  async function measureQuota(): Promise<void> {
    if (measuringQuota || busy) return;
    setMeasuringQuota(true);
    setQuota(null);
    setQuotaError(false);
    try {
      setQuota(await googleDriveApi.measureQuota(csrfToken));
    } catch {
      setQuotaError(true);
    } finally {
      setMeasuringQuota(false);
    }
  }
  const [view, setView] = useState<GoogleDriveIntegrationView | null>(null);
  const [storageCheck, setStorageCheck] = useState<GoogleDriveStorageProbeView | null>(null);
  const [storageCheckError, setStorageCheckError] = useState<string | null>(null);
  const [checkingStorage, setCheckingStorage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reauthenticate, setReauthenticate] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const refresh = async (): Promise<void> => {
    setView(await googleDriveApi.read());
  };
  useEffect(() => {
    let active = true;
    void googleDriveApi.read().then(
      (result) => {
        if (active) setView(result);
      },
      () => {
        if (active) setError('La connexion Google Drive ne peut pas être chargée. Réessayez.');
      },
    );
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    void googleDriveApi.readStorageCheck().then(
      (result) => {
        if (active) setStorageCheck(result);
      },
      () => {
        if (active)
          setStorageCheckError('Le dernier test de stockage ne peut pas être chargé. Réessayez.');
      },
    );
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (storageCheck?.status !== 'RUNNING' || checkingStorage) return;
    let active = true;
    const timer = window.setInterval(() => {
      void Promise.all([googleDriveApi.readStorageCheck(), googleDriveApi.read()]).then(
        ([result, integration]) => {
          if (active) {
            setStorageCheck(result);
            setView(integration);
          }
        },
        () => undefined,
      );
    }, 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [storageCheck?.status, checkingStorage]);

  async function checkStorage(): Promise<void> {
    if (busy || checkingStorage) return;
    setCheckingStorage(true);
    setStorageCheckError(null);
    setReauthenticate(false);
    try {
      setStorageCheck(await googleDriveApi.runStorageCheck(csrfToken));
    } catch (failure) {
      if (failure instanceof GoogleDriveApiError && failure.code === 'REAUTHENTICATION_REQUIRED') {
        setStorageCheckError(
          'Pour vérifier le stockage, reconnectez-vous à Tosho puis recommencez.',
        );
        setReauthenticate(true);
      } else if (
        failure instanceof GoogleDriveApiError &&
        failure.code === 'DRIVE_STORAGE_CHECK_BUSY'
      ) {
        setStorageCheckError(
          'Un test est déjà en cours. Patientez quelques minutes puis rechargez cette page.',
        );
      } else {
        setStorageCheckError(
          'Le test n’a pas pu être terminé. Rechargez cette page pour consulter le résultat enregistré.',
        );
      }
      await googleDriveApi
        .readStorageCheck()
        .then(setStorageCheck)
        .catch(() => undefined);
    } finally {
      await refresh().catch(() => {
        setStorageCheckError(
          (previous) => previous ?? 'L’historique n’a pas pu être actualisé. Rechargez cette page.',
        );
      });
      setCheckingStorage(false);
    }
  }

  const attemptStatus = view?.attempt?.status;
  const pending =
    attemptStatus === 'PENDING' || attemptStatus === 'RECEIVED' || attemptStatus === 'EXCHANGING';
  useEffect(() => {
    if (!pending || busy) return;
    let active = true;
    const timer = window.setInterval(() => {
      void googleDriveApi.read().then(
        (result) => {
          if (active) setView(result);
        },
        () => undefined,
      );
    }, 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [pending, busy]);

  async function run(
    action: 'begin' | 'complete' | 'cancel' | 'disable',
    backupMetadata = false,
  ): Promise<void> {
    if (busy || checkingStorage) return;
    setBusy(true);
    setError(null);
    setReauthenticate(false);
    try {
      if (action === 'begin') {
        window.location.assign(await googleDriveApi.begin(csrfToken, backupMetadata));
        return;
      }
      await googleDriveApi[action](csrfToken);
      setConfirmDisable(false);
      await refresh();
    } catch (failure) {
      if (failure instanceof GoogleDriveApiError && failure.code === 'REAUTHENTICATION_REQUIRED') {
        setError('Pour protéger votre Drive, reconnectez-vous à Tosho puis recommencez.');
        setReauthenticate(true);
      } else if (
        failure instanceof GoogleDriveApiError &&
        failure.code === 'DRIVE_OWNER_CHANGE_REQUIRES_MIGRATION'
      ) {
        setError(
          'Ce Drive appartient à un autre compte. Reconnectez le compte déjà utilisé ; le déplacement des fichiers doit être préparé séparément.',
        );
      } else {
        setError(
          'La connexion n’a pas pu être terminée. Réessayez ; aucun autre Drive n’a été utilisé.',
        );
      }
      // Une réponse perdue ne doit pas laisser un état local « en attente » après un succès serveur.
      await refresh().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  const connection = view?.connection;
  return (
    <div className="app-content drive-integration">
      <header className="drive-integration__heading">
        <div>
          <p className="dashboard-heading__eyebrow">Paramètres · Connexions</p>
          <h1>Google Drive</h1>
          <p className="dashboard-heading__intro">
            Connectez le Drive de votre agence pour préparer le stockage des fichiers de vos
            clients.
          </p>
        </div>
        <button type="button" className="is-secondary" onClick={onBack}>
          Retour aux connexions
        </button>
      </header>
      <nav className="drive-integration__navigation" aria-label="Rubriques Google Drive">
        <button type="button" onClick={() => scrollToSection('drive-connection')}>
          Connexion
        </button>
        <button type="button" onClick={() => scrollToSection('drive-capacity')}>
          Espace disponible
        </button>
        <button type="button" onClick={() => scrollToSection('drive-quota-settings')}>
          Limites
        </button>
        <button type="button" onClick={() => scrollToSection('drive-storage-check')}>
          Stockage
        </button>
        <button type="button" onClick={() => scrollToSection('drive-history')}>
          Historique
        </button>
        <button type="button" onClick={() => scrollToSection('drive-video-check')}>
          Vidéo
        </button>
        <button type="button" onClick={() => scrollToSection('drive-client-folders')}>
          Clients
        </button>
      </nav>
      {error ? (
        <p role="alert" className="drive-integration__error tosho-feedback is-error">
          {error}
        </p>
      ) : null}
      {reauthenticate ? (
        <button type="button" onClick={onReauthenticate}>
          Me reconnecter à Tosho
        </button>
      ) : null}
      <section
        className="drive-integration__grid"
        id="drive-connection"
        aria-label="Connexion Google Drive"
        aria-busy={busy || view === null}
      >
        <article className="drive-integration__card drive-integration__card--dark">
          <header>
            <p className="dashboard-panel__eyebrow">Compte de l’agence</p>
            <h2>Connexion</h2>
          </header>
          {view === null ? (
            <p>{error ? 'Réessayez de charger cette page.' : 'Chargement…'}</p>
          ) : (
            <>
              <p className="drive-integration__account">
                {connection ? connection.accountEmail : 'Aucun Google Drive n’est encore connecté.'}
              </p>
              <p className="drive-integration__security-note">
                <PrototypeIcon name="shield-check" /> Votre mot de passe est saisi uniquement chez
                Google.
              </p>
              <p>
                Choisissez votre compte et autorisez Tosho sur la page Google. Revenez ensuite ici
                pour terminer.
              </p>
              {!view.configured ? (
                <p>La connexion à Google Drive n’est pas encore disponible.</p>
              ) : (
                <div className="drive-integration__actions">
                  {!pending ? (
                    <button
                      type="button"
                      disabled={busy || checkingStorage}
                      onClick={() => void run('begin')}
                    >
                      {busy
                        ? 'Ouverture…'
                        : connection
                          ? 'Reconnecter mon Google Drive'
                          : 'Connecter mon Google Drive'}
                    </button>
                  ) : null}
                  {attemptStatus === 'RECEIVED' ? (
                    <button
                      type="button"
                      disabled={busy || checkingStorage}
                      onClick={() => void run('complete')}
                    >
                      {busy ? 'Vérification…' : 'Terminer la connexion'}
                    </button>
                  ) : null}
                  {pending ? (
                    <button
                      type="button"
                      className="is-secondary"
                      disabled={busy || checkingStorage}
                      onClick={() => void run('cancel')}
                    >
                      Annuler cette tentative
                    </button>
                  ) : null}
                </div>
              )}
              {pending ? (
                <p role="status">
                  {labels[attemptStatus]}. Délai jusqu’au {formatDate(view.attempt!.expiresAt)}.
                </p>
              ) : null}
            </>
          )}
        </article>
        <article className="drive-integration__card drive-integration__card--dark">
          <header>
            <p className="dashboard-panel__eyebrow">État</p>
            <h2>Accès au Drive</h2>
          </header>
          <p
            role="status"
            className={`drive-integration__status${connection?.status === 'VERIFIED' ? ' is-active' : connection?.status === 'DISABLED' ? ' is-disabled' : ''}`}
          >
            {connection?.status === 'VERIFIED'
              ? 'Accès vérifié'
              : connection?.status === 'DISABLED'
                ? 'Désactivé dans Tosho'
                : 'Aucun accès vérifié'}
          </p>
          {connection ? (
            <p>Dernière vérification réussie : {formatDate(connection.verifiedAt)}.</p>
          ) : null}
          <p>
            Cet état indique la dernière vérification réussie. Il ne garantit pas que l’accès
            restera disponible.
          </p>
          {connection?.status === 'VERIFIED' ? (
            <>
              {!confirmDisable ? (
                <button
                  type="button"
                  className="is-secondary"
                  disabled={busy || checkingStorage}
                  onClick={() => setConfirmDisable(true)}
                >
                  Désactiver dans Tosho
                </button>
              ) : (
                <div className="drive-integration__confirmation">
                  <p>
                    Tosho oubliera l’autorisation de ce compte. Les fichiers resteront sur Google
                    Drive. Pour rétablir l’accès, il faudra reconnecter le même compte.
                  </p>
                  <div className="drive-integration__actions">
                    <button
                      type="button"
                      disabled={busy || checkingStorage}
                      onClick={() => void run('disable')}
                    >
                      Confirmer la désactivation
                    </button>
                    <button
                      type="button"
                      className="is-secondary"
                      disabled={busy || checkingStorage}
                      onClick={() => setConfirmDisable(false)}
                    >
                      Conserver la connexion
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </article>
      </section>
      <section
        className="drive-integration__card"
        id="drive-capacity"
        aria-labelledby="drive-capacity-title"
      >
        <header>
          <p className="dashboard-panel__eyebrow">Dernière mesure</p>
          <h2 id="drive-capacity-title">Espace disponible</h2>
        </header>
        <p className="drive-integration__capacity">
          {capacityText(
            connection?.status === 'VERIFIED'
              ? quota
                ? { ...connection, ...quota }
                : connection
              : null,
          )}
        </p>
        <p>
          Cette mesure concerne l’espace global du compte Google, partagé avec ses autres services.
        </p>
        <button
          type="button"
          disabled={busy || measuringQuota || connection?.status !== 'VERIFIED'}
          onClick={() => void measureQuota()}
        >
          {measuringQuota ? 'Mesure en cours…' : 'Actualiser l’espace disponible'}
        </button>
        {quota && connection?.status === 'VERIFIED' && (
          <p role="status">
            Mesure du {dateFormatter.format(new Date(quota.observedAt))}. Ce constat n’autorise pas
            un dépôt.
          </p>
        )}
        {quotaError && (
          <p className="tosho-feedback is-error" role="alert">
            L’espace disponible n’a pas pu être actualisé. Le montant précédent reste une ancienne
            mesure.
          </p>
        )}
      </section>
      <DriveQuotaSettingsCard
        csrfToken={csrfToken}
        enabled={connection?.status === 'VERIFIED'}
        onReauthenticate={onReauthenticate}
      />
      <section className="drive-integration__card" aria-labelledby="drive-backups-title">
        <header>
          <p className="dashboard-panel__eyebrow">Sauvegardes Plesk</p>
          <h2 id="drive-backups-title">Lecture des sauvegardes existantes</h2>
        </header>
        <p>
          Tosho lit les noms, dates et tailles des archives déposées par Plesk. Les archives restent
          chiffrées sur Google Drive ; Plesk continue à gérer leur dépôt et leur restauration.
        </p>
        <p>
          Cette autorisation supplémentaire permet de consulter les métadonnées des fichiers
          existants du Drive. Les fichiers ne sont pas téléchargés.
        </p>
        {connection?.backupMetadataGranted && connection.status === 'VERIFIED' ? (
          <p role="status" className="tosho-feedback is-success">
            Lecture des métadonnées autorisée.
          </p>
        ) : (
          <button
            type="button"
            disabled={busy || checkingStorage || pending || !view?.configured}
            onClick={() => void run('begin', true)}
          >
            Autoriser la lecture des sauvegardes chez Google
          </button>
        )}
        <HostingBackupSettingsCard
          csrfToken={csrfToken}
          enabled={connection?.status === 'VERIFIED' && connection.backupMetadataGranted === true}
          onReauthenticate={onReauthenticate}
        />
      </section>
      <section
        className="drive-integration__card drive-integration__card--beige"
        id="drive-storage-check"
        aria-labelledby="drive-storage-check-title"
        aria-busy={checkingStorage}
      >
        <header>
          <p className="dashboard-panel__eyebrow">Vérification réelle</p>
          <h2 id="drive-storage-check-title">Stockage des fichiers</h2>
        </header>
        <p>
          Tosho crée un petit fichier texte privé sur votre Drive, le relit et vérifie sa mise à la
          corbeille puis sa restauration. Le fichier de test est ensuite remis à la corbeille.
        </p>
        <p>
          Aucun fichier client n’est utilisé. Aucun fichier n’est enregistré sur l’hébergement
          Tosho.
        </p>
        <button
          type="button"
          disabled={busy || checkingStorage || connection?.status !== 'VERIFIED'}
          onClick={() => void checkStorage()}
        >
          {checkingStorage ? 'Vérification en cours…' : 'Vérifier le stockage'}
        </button>
        {storageCheckError ? (
          <p className="tosho-feedback is-error" role="alert">
            {storageCheckError}
          </p>
        ) : null}
        <p role="status">
          {checkingStorage || storageCheck?.status === 'RUNNING'
            ? 'Test en cours. Le résultat sera enregistré, même si vous quittez cette page.'
            : storageCheck?.status === 'VERIFIED'
              ? 'Test du fichier privé réussi.'
              : 'Stockage non vérifié.'}
        </p>
        {storageCheck ? (
          <>
            {storageCheck.checkedAt ? (
              <p>
                Dernier test : {formatDate(storageCheck.checkedAt)}. Ce résultat décrit ce test à
                cette date.
              </p>
            ) : null}
            <StorageCheckResults results={storageCheck} />
            {!storageCheck.cleanupVerified && storageCheck.status !== 'RUNNING' ? (
              <p>
                Le rangement du fichier de test reste à vérifier. Le prochain essai reprendra ce
                nettoyage avant un nouveau test.
              </p>
            ) : null}
          </>
        ) : null}
        <p>
          Ce test ne valide pas encore la lecture des vidéos sur Safari ou iPhone. Le dépôt des
          fichiers clients et les réglages par tableau seront ajoutés ensuite.
        </p>
      </section>
      <DriveVideoProbeCard
        csrfToken={csrfToken}
        enabled={!busy && connection?.status === 'VERIFIED'}
        onRefresh={refresh}
        onReauthenticate={onReauthenticate}
      />
      <DriveClientFolderCheckCard
        csrfToken={csrfToken}
        enabled={!busy && connection?.status === 'VERIFIED'}
        onRefresh={refresh}
        onReauthenticate={onReauthenticate}
      />
      <section
        className="drive-integration__card drive-integration__card--dark"
        id="drive-history"
        aria-labelledby="drive-history-title"
      >
        <header>
          <p className="dashboard-panel__eyebrow">Traçabilité</p>
          <h2 id="drive-history-title">Historique du connecteur</h2>
        </header>
        {!view?.history.length ? (
          <p>Aucune activité enregistrée.</p>
        ) : (
          <ul className="drive-integration__history">
            {view.history.map((item) => (
              <li key={`${item.kind ?? 'CONNECTION'}-${item.id}`}>
                <span>{formatDate(item.createdAt)}</span>
                {item.kind === 'STORAGE_CHECK' ? (
                  <div className="drive-integration__history-result">
                    <strong>{storageHistoryLabel(item)}</strong>
                    {item.imported ? <p>Repris du dernier résultat enregistré.</p> : null}
                    {item.status === 'INTERRUPTED' ? (
                      <p>Le test n’a pas enregistré de résultat final.</p>
                    ) : null}
                    {item.status === 'CLEANED' ? (
                      <p>Le nettoyage est vérifié. Vous pouvez lancer un nouveau test complet.</p>
                    ) : null}
                    <details>
                      <summary>Voir les cinq contrôles</summary>
                      <StorageCheckResults results={item.results} />
                    </details>
                  </div>
                ) : item.kind === 'VIDEO_CHECK' ? (
                  <strong>{videoCheckLabels[item.status]}</strong>
                ) : item.kind === 'CLIENT_FOLDERS' ? (
                  <div className="drive-integration__history-result">
                    <strong>
                      {item.clientName} · {clientFolderCheckLabels[item.status]}
                    </strong>
                    <p>Contrôle ponctuel des dossiers, pas une autorisation de dépôt.</p>
                  </div>
                ) : (
                  <strong>{labels[item.status] ?? 'État non vérifié'}</strong>
                )}
              </li>
            ))}
          </ul>
        )}
        <p>
          Les dix dernières activités sont affichées : connexions et contrôles de stockage, vidéo ou
          dossiers Client. Une vidéo prête ne prouve pas encore sa lecture sur votre appareil.
        </p>
      </section>
    </div>
  );
}

function StorageCheckResults({
  results,
}: {
  readonly results: GoogleDriveStorageCheckResults;
}): React.JSX.Element {
  return (
    <ul className="drive-integration__checks" aria-label="Résultats du test de stockage">
      {(
        [
          ['Dépôt privé sur Drive', results.privateWriteVerified],
          ['Relecture complète', results.fullReadVerified],
          ['Lecture d’un extrait', results.rangeReadVerified],
          ['Corbeille et restauration du fichier de test', results.trashRestoreVerified],
          ['Fichier de test remis à la corbeille', results.cleanupVerified],
        ] as const
      ).map(([label, verified]) => (
        <li key={label}>
          <span>{label}</span>
          <strong>{verified ? 'Vérifié' : 'Non vérifié'}</strong>
        </li>
      ))}
    </ul>
  );
}

function storageHistoryLabel(
  item: Extract<GoogleDriveHistoryEntry, { kind: 'STORAGE_CHECK' }>,
): string {
  if (item.recovering) {
    if (item.status === 'RUNNING') return 'Nettoyage du fichier de test en cours';
    if (item.status === 'CLEANED') return 'Nettoyage du fichier de test terminé';
    if (item.status === 'INTERRUPTED') return 'Nettoyage du fichier de test interrompu';
    return 'Nettoyage du fichier de test non vérifié';
  }
  if (item.status === 'VERIFIED') return 'Test du stockage réussi';
  if (item.status === 'RUNNING') return 'Test du stockage en cours';
  if (item.status === 'INTERRUPTED') return 'Test du stockage interrompu';
  return 'Test du stockage non vérifié';
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? dateFormatter.format(date) : 'Date non vérifiée';
}

function capacityText(connection: GoogleDriveIntegrationView['connection'] | undefined): string {
  if (
    !connection ||
    connection.limitBytes === null ||
    connection.usageBytes === null ||
    !/^[0-9]{1,19}$/u.test(connection.limitBytes) ||
    !/^[0-9]{1,19}$/u.test(connection.usageBytes)
  )
    return 'Espace disponible non vérifié.';
  const limit = BigInt(connection.limitBytes),
    usage = BigInt(connection.usageBytes);
  if (usage > limit) return 'L’espace du compte Google est dépassé.';
  // Affichage uniquement : la décision de dépôt conservera les entiers exacts et ses réservations.
  const gigabytes = Number((limit - usage) / 1000000n) / 1000;
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(gigabytes)} Go disponibles lors de la dernière vérification.`;
}
